/**
 * ENG-9637: a ceiling on how often this manager may actually poll, that holds
 * no matter how many callers ask.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE FORK FIX
 * --------------------------------------------
 * The fork fix (`single-flight.ts` + `poll-loop-guard.ts`) removed the one
 * mechanism we know of that multiplied the poll rate: a coalesced caller
 * re-arming the timer, forking a permanent untracked chain. That is a fix for
 * a CAUSE. It is not a bound.
 *
 * The measurement is what argues for a bound as well. One agent reached ~1 Hz
 * against a fleet mean of 1.7 polls/min and stayed there for 13 days, taking
 * over half of all `/host/refresh` requests, and nothing in the system treated
 * that as a condition worth acting on. Whatever produces concurrent poll
 * passes next — a new caller, a retry loop, a future edit to the scheduler —
 * would ramp exactly as invisibly. A cause-fix protects against the bug we
 * found; a ceiling protects against the shape of the bug.
 *
 * WHAT IT BOUNDS, AND WHAT IT DOES NOT
 * ------------------------------------
 * It bounds REQUESTS, which is what costs money: a throttled call returns
 * without touching the network. It does NOT collapse surplus poll chains, and
 * it deliberately does not try to — a chain is a `setTimeout` handle nothing
 * holds a reference to, so it is unreachable by construction and only a
 * process restart removes it. Surplus chains under this limiter keep waking up
 * and keep being turned away, which is cheap and, unlike their previous
 * behaviour, visible in the log.
 *
 * THE SHAPE: TOKEN BUCKET, NOT MINIMUM SPACING
 * --------------------------------------------
 * Minimum spacing ("refuse anything within N ms of the last poll") is simpler
 * and wrong here, because `triggerEarlyPoll` exists precisely to poll sooner
 * than the schedule when a Realtime event says the world changed. Flat spacing
 * would convert every early poll into a delayed one, trading a cost bug for a
 * latency bug — the same trade already rejected for the one-line `clearTimeout`
 * fix in this ticket.
 *
 * A bucket keeps both properties: `burst` credits are available for genuine
 * early polls, and the SUSTAINED rate can never exceed one poll per
 * `intervalMs` however many callers arrive. Healthy managers never notice it.
 * The scheduled loop uses a fixed GAP (`delayMs` is applied AFTER the cycle
 * completes), so its true period is `intervalMs + cycleDuration` — strictly
 * slower than the bucket refills. A single-chain manager therefore sits at a
 * full bucket permanently and every early poll it wants is granted.
 *
 * THE STARVATION BOUND, AND WHY THERE IS NO SEPARATE BACKSTOP
 * -----------------------------------------------------------
 * A rate limiter on the poll loop is a new way to build the failure ENG-9068
 * exists to prevent: a manager that is alive, heartbeating, and never polls
 * again. That risk has to be answered, and the first draft of this module
 * answered it with a `forceAfterMs` override — "allow a poll regardless of the
 * bucket once this long has passed since the last one".
 *
 * That override was DELETED, because it could never fire, and a safety net
 * that cannot fire is worse than none: it reads as protection while providing
 * none. The proof is short. Refill is purely a function of elapsed time, and
 * `burst` is clamped to at least 1 — so `tokens < 1` implies less than
 * `intervalMs` has passed since the bucket was last drained. Any override
 * threshold above one interval is therefore unreachable: by the time it
 * elapsed, the bucket itself would already have granted. Its test could only
 * ever have been green.
 *
 * What replaces it is stronger, because it is a property rather than a patch:
 *
 *     NO SEQUENCE OF CALLS CAN BE DENIED FOR LONGER THAN `intervalMs`.
 *
 * That holds by construction, it is what `denies for at most one interval`
 * asserts in the test file, and it means the worst a defect in this file can
 * do to a manager is slow it to the configured cadence — never stop it. The
 * clamping in `positiveOr` and the `burst >= 1` floor are what the proof rests
 * on, which is why both are enforced at construction rather than documented.
 *
 * Time is read from a MONOTONIC clock, not `Date.now()`. A wall-clock step
 * (NTP correction, VM resume, a host whose clock is being fixed) would
 * otherwise either hand out a windfall of tokens or freeze the bucket, and a
 * host having clock trouble is not a host that should also start polling at
 * 1 Hz or stop polling entirely. A rewind is therefore unreachable in
 * production and is still handled explicitly in `refill` — see the comment
 * there for why both of the obvious answers break one of the two properties
 * this module has to hold at once.
 */

/**
 * Fallback ceiling used only when the manager's configured interval is not
 * available yet. Matches `agt manager start`'s `--interval` default of 10s
 * (`apps/cli/src/commands/manager.ts`), so the fallback is the same cadence the
 * fleet actually runs rather than an invented one.
 */
export const DEFAULT_POLL_INTERVAL_MS = 10_000;

/** Default burst credit: how many early polls may be granted back-to-back. */
export const DEFAULT_POLL_BURST = 3;

export interface PollRateLimiterOptions {
  /**
   * The sustained ceiling: at most one poll per `intervalMs`, amortised. Pass
   * the manager's configured poll interval — the ceiling is then "the cadence
   * you asked for", and any excess above it is by definition unintended.
   */
  intervalMs: number;
  /**
   * Credits available for bursts (early polls). Default
   * {@link DEFAULT_POLL_BURST}, and floored at 1: a value below 1 would make
   * the bucket unable to ever grant, which is the one way this module could
   * stop a manager's poll loop. See the starvation bound above.
   */
  burst?: number;
  /**
   * Monotonic clock, in milliseconds. Injected for tests; the default is
   * `performance.now()`, which is monotonic and unaffected by wall-clock steps.
   */
  now?: () => number;
}

export type PollRateVerdict =
  /** The bucket had a credit. */
  | 'allowed'
  /** The bucket was empty. No poll happens, and no request is made. */
  | 'throttled';

export interface PollRateDecision {
  allowed: boolean;
  verdict: PollRateVerdict;
  /**
   * Consecutive throttled calls INCLUDING this one, or 0 when allowed. The
   * caller logs on the transitions rather than on every call, so a manager
   * carrying many surplus chains reports the condition without flooding
   * `manager.log` with one line per rejected wake-up.
   */
  consecutiveThrottled: number;
  /** Total calls turned away since the limiter was created. */
  totalThrottled: number;
}

export interface PollRateLimiter {
  /**
   * Ask permission to run one poll pass. Consumes a credit when it grants one.
   *
   * Never throws: a limiter that can throw would reject the cycle, and a
   * rejected cycle is the ENG-9068 lane where re-arming has to happen without
   * knowing what went wrong. The arithmetic below has no throwing operations,
   * and the constructor clamps its inputs so it cannot be handed values that
   * would produce `NaN`.
   */
  tryAcquire: () => PollRateDecision;
  /** Total calls turned away. Exposed for diagnostics and assertions. */
  totalThrottled: () => number;
  /** Credits currently available, for assertions. Fractional by design. */
  tokens: () => number;
}

/**
 * Clamp to a finite, strictly-positive number.
 *
 * Every input here divides or multiplies into `tokens`. A `NaN`, `0` or
 * negative value would make `tokens` `NaN` — and `NaN >= 1` is false, so the
 * bucket would deny FOREVER and the backstop would become the entire poll
 * schedule. Clamping at construction is the cheapest place to make that
 * unreachable, and it is why `tryAcquire` can promise not to throw.
 */
function positiveOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

export function createPollRateLimiter(opts: PollRateLimiterOptions): PollRateLimiter {
  const intervalMs = positiveOr(opts.intervalMs, 10_000);
  // Floored at 1, not merely required to be positive. `positiveOr` accepts
  // 0.5, and a burst of 0.5 caps `tokens` below the threshold `tryAcquire`
  // tests — the bucket would then deny for the life of the process. This floor
  // is what the starvation bound in the docstring rests on.
  const burst = Math.max(1, positiveOr(opts.burst, DEFAULT_POLL_BURST));
  const now = opts.now ?? (() => performance.now());

  // Start full: the very first poll of a process must never be throttled, and
  // a manager that has just restarted is the one with the most to catch up on.
  let tokens = burst;
  let lastRefillAt = now();
  let consecutiveThrottled = 0;
  let totalThrottled = 0;

  function refill(at: number): void {
    if (at < lastRefillAt) {
      // THE CLOCK WENT BACKWARDS. `performance.now()` is monotonic so this is
      // unreachable in production; it is reachable through an injected clock,
      // and the handling is worth stating because two obvious answers are both
      // wrong (CodeRabbit, #5188).
      //
      // Clamping elapsed to 0 while still rebasing — the first version — leaves
      // the ceiling open: after a rewind the clock re-traverses a span it has
      // already been credited for, and those credits are handed out a second
      // time. Over a long rewind that is many extra polls, which is precisely
      // what this module exists to prevent.
      //
      // Refusing to rebase (`lastRefillAt = Math.max(lastRefillAt, at)`) closes
      // that hole and opens a worse one: until the clock climbs back past its
      // old high-water mark, elapsed is 0 and NOTHING refills. A one-hour
      // rewind is then an hour of a manager that is alive, heartbeating and
      // never polling — the ENG-9068 failure, and it would silently break this
      // module's own stated bound that no sequence of calls can be denied for
      // longer than `intervalMs`.
      //
      // So: rebase onto the new timeline AND forfeit the accumulated credit.
      // Nothing carries across the discontinuity, so the rewound span cannot be
      // spent twice; and refill resumes immediately, so the starvation bound
      // still holds — at most one interval of denial, measured on the timeline
      // the caller is now on.
      tokens = 0;
      lastRefillAt = at;
      return;
    }
    const elapsed = at - lastRefillAt;
    lastRefillAt = at;
    tokens = Math.min(burst, tokens + elapsed / intervalMs);
  }

  function tryAcquire(): PollRateDecision {
    const at = now();
    refill(at);

    if (tokens >= 1) {
      tokens -= 1;
      consecutiveThrottled = 0;
      return { allowed: true, verdict: 'allowed', consecutiveThrottled: 0, totalThrottled };
    }

    consecutiveThrottled += 1;
    totalThrottled += 1;
    return { allowed: false, verdict: 'throttled', consecutiveThrottled, totalThrottled };
  }

  return {
    tryAcquire,
    totalThrottled: () => totalThrottled,
    tokens: () => tokens,
  };
}
